import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  FileText, RefreshCw, Loader2, Send, Download, Ban, CalendarRange,
  AlertTriangle, Shield, CheckCircle2, Landmark,
} from 'lucide-react';
import API_BASE from '../../utils/apiBase';
import HelpSectionHint from '../../components/portal/HelpSectionHint';

const API = API_BASE;

const STATUS_STYLES = {
  draft: 'bg-slate-50 text-slate-700 border-slate-200',
  sent: 'bg-blue-50 text-blue-800 border-blue-200',
  paid: 'bg-green-50 text-green-700 border-green-200',
  credited: 'bg-purple-50 text-purple-700 border-purple-200',
  voided: 'bg-red-50 text-red-700 border-red-200',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const toLocalInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInputValue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const formatMoney = (amount, currency = 'GBP') => {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(amount));
};

export default function InvoicesPage() {
  const { token, isAdmin } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [invoices, setInvoices] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [correctionSummary, setCorrectionSummary] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [eligible, setEligible] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [organisationId, setOrganisationId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [generateNotes, setGenerateNotes] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditHours, setCreditHours] = useState({});

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const url = `${API}/invoices${params.toString() ? `?${params}` : ''}`;
      const res = await axios.get(url, { headers });
      setInvoices(res.data?.invoices || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Invoices are admin-only');
      } else {
        toast.error('Failed to load invoices');
      }
    } finally {
      setLoading(false);
    }
  }, [headers, statusFilter]);

  const fetchOrgs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/organisations`, { headers });
      setOrgs((res.data?.organisations || []).filter((o) => o.is_active !== false));
    } catch {
      setOrgs([]);
    }
  }, [headers]);

  const fetchEligible = useCallback(async () => {
    const start = fromLocalInputValue(periodStart);
    const end = fromLocalInputValue(periodEnd);
    if (!organisationId || !start || !end) {
      setEligible(null);
      return;
    }
    try {
      const params = new URLSearchParams({
        organisation_id: organisationId,
        pay_period_start: start,
        pay_period_end: end,
      });
      const res = await axios.get(`${API}/invoices/eligible-shifts?${params}`, { headers });
      setEligible(res.data);
    } catch {
      setEligible(null);
    }
  }, [headers, organisationId, periodStart, periodEnd]);

  const applyPeriodPreset = useCallback(async (preset) => {
    try {
      const res = await axios.get(`${API}/timesheets/suggested-period?preset=${preset}`, { headers });
      const start = res.data?.pay_period_start;
      const end = res.data?.pay_period_end;
      if (start) setPeriodStart(toLocalInputValue(start));
      if (end) setPeriodEnd(toLocalInputValue(end));
      return res.data;
    } catch {
      toast.error('Could not load billing period');
      return null;
    }
  }, [headers]);

  useEffect(() => {
    if (!isAdmin()) return;
    fetchInvoices();
    fetchOrgs();
    applyPeriodPreset('last_week');
  }, [fetchInvoices, fetchOrgs, applyPeriodPreset, isAdmin]);

  useEffect(() => {
    if (!isAdmin()) return;
    fetchEligible();
  }, [fetchEligible, isAdmin]);

  const fetchDetail = useCallback(async (id) => {
    if (!id) return;
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/invoices/${id}`, { headers });
      setDetail(res.data?.invoice || null);
      setIntegrity(res.data?.integrity || null);
      setCorrectionSummary(res.data?.correction_summary || null);
      setCreditReason('');
      setCreditHours({});
    } catch {
      toast.error('Failed to load invoice');
      setDetail(null);
      setIntegrity(null);
      setCorrectionSummary(null);
    } finally {
      setDetailLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
    else {
      setDetail(null);
      setIntegrity(null);
      setCorrectionSummary(null);
    }
  }, [selectedId, fetchDetail]);

  const formatApiError = (err, fallback) => {
    const data = err?.response?.data;
    const detail = data?.detail ?? data?.message;
    if (typeof detail === 'string') return detail;
    if (detail?.message) {
      const missing = detail.missing_roles || [];
      if (missing.length) {
        return `${detail.message}: ${missing.join(', ')}`;
      }
      return detail.message;
    }
    return fallback;
  };

  const handleGenerate = async () => {
    const start = fromLocalInputValue(periodStart);
    const end = fromLocalInputValue(periodEnd);
    if (!organisationId) {
      toast.error('Select a client organisation');
      return;
    }
    if (!start || !end) {
      toast.error('Set billing period start and end');
      return;
    }
    if (eligible?.blocked_count > 0) {
      toast.error('Some shifts are missing charge rates — set rates on the organisation first');
      return;
    }
    if (!eligible?.ready_count) {
      toast.error('No approved uninvoiced shifts for this organisation and period');
      return;
    }
    const orgName = orgs.find((o) => o.id === organisationId)?.name || 'client';
    if (!window.confirm(
      `Create draft invoice for ${orgName} (${eligible.ready_count} shift(s), est. ${formatMoney(eligible.estimated_subtotal)})?`,
    )) return;

    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API}/invoices/generate`,
        {
          organisation_id: organisationId,
          pay_period_start: start,
          pay_period_end: end,
          notes: generateNotes || undefined,
        },
        { headers },
      );
      const invoice = res.data?.invoice;
      toast.success(`Invoice ${invoice?.reference || ''} created (draft)`);
      await fetchInvoices();
      await fetchEligible();
      if (invoice?.id) setSelectedId(invoice.id);
    } catch (err) {
      toast.error(formatApiError(err, 'Generate failed'));
    } finally {
      setActionLoading(false);
    }
  };

  const runAction = async (suffix, body, successMsg) => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/invoices/${selectedId}${suffix}`, body || {}, { headers });
      toast.success(successMsg);
      await fetchInvoices();
      await fetchDetail(selectedId);
    } catch (err) {
      toast.error(formatApiError(err, 'Action failed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSend = () => {
    const label = detail?.document_type === 'credit_note' ? 'credit note' : 'invoice';
    if (integrity && !integrity.ok) {
      toast.error(`Cannot send: ${label} integrity checks are failing`);
      return;
    }
    if (!window.confirm(`Send this ${label} to the client? Once sent, its financial content is immutable.`)) return;
    runAction('/send', {}, `${detail?.document_type === 'credit_note' ? 'Credit note' : 'Invoice'} sent to client`);
  };

  const handleMarkPaid = () => {
    runAction('/mark-paid', { payment_reference: paymentReference || undefined }, 'Invoice marked paid');
  };

  const handleVoid = () => {
    if (!window.confirm('Void this DRAFT document? Draft invoice shifts will be released for re-billing.')) return;
    runAction('/void', {}, 'Draft voided');
  };

  const handleCreateCreditNote = async () => {
    if (!selectedId || !detail || detail.document_type === 'credit_note') return;
    const adjustments = (detail.items || [])
      .map((item) => ({
        attendance_record_id: item.attendance_record_id,
        credit_hours: Number(creditHours[item.attendance_record_id] || 0),
      }))
      .filter((item) => item.attendance_record_id && item.credit_hours > 0);
    if (!creditReason.trim()) {
      toast.error('Add the reason for the correction');
      return;
    }
    if (!adjustments.length) {
      toast.error('Enter credit hours against at least one invoice line');
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API}/invoices/${selectedId}/credit-note`,
        { reason: creditReason.trim(), adjustments },
        { headers },
      );
      const note = res.data?.credit_note;
      toast.success(`Credit note ${note?.reference || ''} created as draft`);
      await fetchInvoices();
      await fetchDetail(selectedId);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not create credit note'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReleaseForRebilling = async () => {
    if (!window.confirm('This invoice is fully credited. Release its original attendance so a linked corrected replacement can be generated?')) return;
    await runAction('/release-for-rebilling', {}, 'Original invoice credited — attendance released for corrected rebilling');
  };

  const handleGenerateReplacement = async () => {
    if (!detail || detail.status !== 'credited') return;
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API}/invoices/generate`,
        {
          organisation_id: detail.organisation_id,
          pay_period_start: detail.pay_period_start,
          pay_period_end: detail.pay_period_end,
          replacement_for_invoice_id: detail.id,
          notes: `Corrected replacement for ${detail.reference}`,
        },
        { headers },
      );
      const replacement = res.data?.invoice;
      toast.success(`Corrected invoice ${replacement?.reference || ''} created as draft`);
      await fetchInvoices();
      if (replacement?.id) setSelectedId(replacement.id);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not generate corrected replacement'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      const res = await axios.get(`${API}/invoices/${selectedId}/pdf`, {
        headers,
        responseType: 'blob',
      });
      const ref = detail?.reference || selectedId;
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${ref}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Invoice PDF downloaded');
    } catch (err) {
      toast.error(formatApiError(err, 'PDF download failed'));
    } finally {
      setActionLoading(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center space-y-4">
        <Shield className="h-12 w-12 text-text-muted mx-auto" />
        <h1 className="text-xl font-heading font-semibold">Invoices — admin only</h1>
        <p className="text-sm text-text-muted">
          Client billing is restricted to admin accounts.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-7xl mx-auto" data-testid="invoices-page">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-text-primary flex items-center gap-2">
            <FileText className="h-7 w-7 text-primary" />
            Invoices
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Bill clients from canonical shift evidence (admin only). New attendance uses the approved hours confirmed by the care home; legacy pre-gate records retain their historical scheduled-hours basis. Payroll remains a separate truth.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchInvoices} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card data-help-section="organisation">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarRange className="h-5 w-5" />
            Generate draft invoice
            <HelpSectionHint sectionId="organisation" />
          </CardTitle>
          <p className="text-sm text-text-muted">
            Every draft is built from the canonical shift, worker, care-home, approved attendance and client-confirmed billing quantity. Charge rates are selected from the client rate-card version that applied on each shift's service date.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="sm:col-span-2">
              <Label htmlFor="invoice-org">Client organisation</Label>
              <select
                id="invoice-org"
                value={organisationId}
                onChange={(e) => setOrganisationId(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="">Select organisation…</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="invoice-period-start">Period start</Label>
              <Input
                id="invoice-period-start"
                type="datetime-local"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="invoice-period-end">Period end</Label>
              <Input
                id="invoice-period-end"
                type="datetime-local"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="invoice-notes">Notes (optional)</Label>
              <Input
                id="invoice-notes"
                value={generateNotes}
                onChange={(e) => setGenerateNotes(e.target.value)}
                placeholder="Internal or client-facing notes"
              />
            </div>
          </div>

          {eligible && (
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm space-y-2">
              <div className="flex flex-wrap gap-4">
                <span><strong>{eligible.shift_count}</strong> shift(s) in period</span>
                <span className="text-green-700"><CheckCircle2 className="inline h-4 w-4 mr-1" />{eligible.ready_count} ready</span>
                {eligible.blocked_count > 0 && (
                  <span className="text-amber-700">
                    <AlertTriangle className="inline h-4 w-4 mr-1" />
                    {eligible.blocked_count} missing charge rate(s)
                  </span>
                )}
                <span>Est. subtotal: <strong>{formatMoney(eligible.estimated_subtotal)}</strong></span>
              </div>

              {eligible.shifts?.length > 0 && (
                <div className="overflow-x-auto rounded border border-slate-200 bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-2 py-2">Shift</th>
                        <th className="text-left px-2 py-2">Role</th>
                        <th className="text-right px-2 py-2">Scheduled</th>
                        <th className="text-right px-2 py-2">Approved</th>
                        <th className="text-right px-2 py-2">Billable</th>
                        <th className="text-right px-2 py-2">Rate</th>
                        <th className="text-right px-2 py-2">Line total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligible.shifts.map((shift) => (
                        <tr key={shift.attendance_record_id} className="border-t">
                          <td className="px-2 py-2">{formatDateTime(shift.shift_start_at)}</td>
                          <td className="px-2 py-2">{shift.role_required || '—'}</td>
                          <td className="px-2 py-2 text-right">{Number(shift.hours_scheduled || 0).toFixed(2)}h</td>
                          <td className="px-2 py-2 text-right">{Number(shift.hours_actual || 0).toFixed(2)}h</td>
                          <td className="px-2 py-2 text-right font-semibold">{Number(shift.hours_billed || 0).toFixed(2)}h</td>
                          <td className="px-2 py-2 text-right">
                            <div>{formatMoney(shift.rate_amount)}</div>
                            {shift.charge_rate_effective_from && (
                              <div className="text-[10px] text-text-muted whitespace-nowrap">
                                from {new Date(shift.charge_rate_effective_from).toLocaleDateString('en-GB')}
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right">{formatMoney(shift.estimated_line_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="px-2 py-2 text-[11px] text-slate-600 border-t">
                    Billable hours are the canonical quantity CareOps will place on the invoice. Payroll hours remain separate.
                  </p>
                </div>
              )}

              {eligible.blocked_count > 0 && eligible.missing_charge_rates && eligible.missing_charge_rates.length > 0 && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-xs">
                  <p className="font-medium mb-1">Missing charge rates for these roles (must be set on the selected organisation in Organisations → Manage rates):</p>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {eligible.missing_charge_rates.map((m, idx) => (
                      <li key={idx}>
                        <strong>{m.role_required || '(unspecified role)'}</strong>
                        {m.shift_id && <span className="text-amber-600"> (shift {m.shift_id.slice(0,8)}…)</span>}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1 text-amber-700">Add the exact matching role names and rates, then refresh or re-select the period.</p>
                </div>
              )}
              {eligible.shift_count === 0 && eligible.diagnostics && (
                <div className="text-amber-800 space-y-1 pt-1 border-t border-amber-100">
                  <p>
                    <strong>{eligible.diagnostics.approved_total}</strong> approved attendance record(s) in the system, but none qualify for this client and period.
                  </p>
                  {eligible.diagnostics.missing_organisation > 0 && (
                    <p>• <strong>{eligible.diagnostics.missing_organisation}</strong> shift(s) have no billing client — open <strong>Shifts → View → Billing client</strong> and set Test Organisation.</p>
                  )}
                  {eligible.diagnostics.different_organisation > 0 && (
                    <p>• <strong>{eligible.diagnostics.different_organisation}</strong> linked to a different organisation.</p>
                  )}
                  {eligible.diagnostics.outside_period > 0 && (
                    <p>• <strong>{eligible.diagnostics.outside_period}</strong> clock-out outside this date range — widen the period.</p>
                  )}
                  {eligible.diagnostics.already_invoiced > 0 && (
                    <p>• <strong>{eligible.diagnostics.already_invoiced}</strong> already on an invoice.</p>
                  )}
                  {eligible.diagnostics.missing_shift > 0 && (
                    <p>• <strong>{eligible.diagnostics.missing_shift}</strong> approved attendance record(s) have no canonical shift — blocked.</p>
                  )}
                  {eligible.diagnostics.worker_mismatch > 0 && (
                    <p>• <strong>{eligible.diagnostics.worker_mismatch}</strong> attendance record(s) do not match the assigned worker — blocked.</p>
                  )}
                  {eligible.diagnostics.missing_clock > 0 && (
                    <p>• <strong>{eligible.diagnostics.missing_clock}</strong> attendance record(s) are missing clock-in/out — correct attendance first.</p>
                  )}
                  {eligible.diagnostics.awaiting_client_confirmation > 0 && (
                    <p>• <strong>{eligible.diagnostics.awaiting_client_confirmation}</strong> shift(s) await care-home confirmation of approved hours.</p>
                  )}
                  {eligible.diagnostics.disputed_by_client > 0 && (
                    <p>• <strong>{eligible.diagnostics.disputed_by_client}</strong> shift(s) are disputed by the care home — resolve before invoicing.</p>
                  )}
                </div>
              )}
            </div>
          )}

          <Button onClick={handleGenerate} disabled={actionLoading || !eligible?.ready_count}>
            {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            Generate draft invoice
          </Button>
          <p className="text-[11px] text-text-muted pt-2">Charge rates must exactly match the role on the shift (enforced by standard role dropdowns in Organisations and Shifts). Paid invoices + locked payroll close the pipeline to Financially Closed.</p>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-5">
        <Card className="lg:col-span-2" data-help-section="send">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-lg flex items-center gap-2">
                Invoices & credit notes
                <HelpSectionHint sectionId="send" />
              </CardTitle>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-gray-200"
              >
                <option value="">All statuses</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="credited">Credited</option>
                <option value="voided">Voided</option>
              </select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-text-muted text-sm">Loading…</div>
            ) : invoices.length === 0 ? (
              <div className="p-8 text-center text-text-muted text-sm">No invoices yet. Set billing client + charge rates on the target Organisation, approve shifts with that client under Shifts, then generate here.</div>
            ) : (
              <ul className="divide-y divide-gray-50 max-h-[480px] overflow-y-auto">
                {invoices.map((inv) => (
                  <li key={inv.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(inv.id)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                        selectedId === inv.id ? 'bg-primary/5 border-l-2 border-primary' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm">
                          {inv.reference}
                          {inv.document_type === 'credit_note' ? ' · Credit note' : ''}
                        </span>
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className={STATUS_STYLES[inv.status] || ''}>
                            {inv.status}
                          </Badge>
                          {inv.document_type !== 'credit_note' && (inv.status === 'sent' || inv.status === 'paid') && (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Advances to Financially Closed (with payroll)</Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-text-muted mt-1 flex items-center gap-1">
                        <Landmark className="h-3 w-3" />
                        {inv.organisation_name || '—'}
                      </p>
                      <p className="text-xs text-text-muted">{formatMoney(inv.total_amount)} · {inv.line_count} lines</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3" data-help-section="send">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              Invoice detail
              <HelpSectionHint sectionId="send" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedId ? (
              <p className="text-sm text-text-muted py-8 text-center">Select an invoice to view details</p>
            ) : detailLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : detail ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold">{detail.reference}</h2>
                      <Badge variant="outline">
                        {detail.document_type === 'credit_note' ? 'Credit note' : 'Invoice'}
                      </Badge>
                    </div>
                    <p className="text-sm text-text-muted">{detail.organisation_name}</p>
                    {detail.document_type === 'credit_note' && (
                      <p className="text-xs text-purple-700 mt-1">
                        Corrects {detail.original_invoice_reference || 'original invoice'} · {detail.correction_reason || 'Invoice correction'}
                      </p>
                    )}
                    <p className="text-xs text-text-muted mt-1">
                      Period: {formatDateTime(detail.pay_period_start)} – {formatDateTime(detail.pay_period_end)}
                    </p>
                  </div>
                  <Badge variant="outline" className={STATUS_STYLES[detail.status] || ''}>
                    {detail.status}
                  </Badge>
                </div>
                {(detail.status === 'sent' || detail.status === 'paid') && detail.document_type !== 'credit_note' && (
                  <p className="text-xs text-emerald-700 -mt-2">This invoice contributes to Financially Closed once the parallel payroll batch for the same period is also locked/exported.</p>
                )}

                {integrity && (
                  <div className={`rounded-lg border p-3 text-sm ${integrity.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
                    <p className="font-medium flex items-center gap-2">
                      {integrity.ok ? <Shield className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                      {integrity.ok ? 'Invoice evidence integrity checks passed' : 'Do not send — invoice evidence needs attention'}
                    </p>
                    {!integrity.ok && (
                      <ul className="mt-2 list-disc pl-5 space-y-1 text-xs">
                        {(integrity.issues || []).map((issue) => <li key={issue}>{issue}</li>)}
                      </ul>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-gray-50">
                    <p className="text-xs text-text-muted">Subtotal</p>
                    <p className="font-semibold">{formatMoney(detail.subtotal)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50">
                    <p className="text-xs text-text-muted">VAT</p>
                    <p className="font-semibold">{formatMoney(detail.vat_amount)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-primary/5">
                    <p className="text-xs text-text-muted">{detail.document_type === 'credit_note' ? 'Total credit' : 'Total due'}</p>
                    <p className="font-semibold text-primary">{formatMoney(detail.total_amount)}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={handleDownloadPdf} disabled={actionLoading || detail.status === 'voided'}>
                    <Download className="h-4 w-4 mr-1" /> PDF
                  </Button>
                  {detail.status === 'draft' && (
                    <Button size="sm" onClick={handleSend} disabled={actionLoading || (integrity && !integrity.ok)}>
                      <Send className="h-4 w-4 mr-1" /> Send {detail.document_type === 'credit_note' ? 'credit note' : 'to client'}
                    </Button>
                  )}
                  {detail.document_type !== 'credit_note' && (detail.status === 'sent' || detail.status === 'draft') && (
                    <>
                      <Input
                        className="max-w-[180px] h-9 text-sm"
                        placeholder="Payment ref (optional)"
                        value={paymentReference}
                        onChange={(e) => setPaymentReference(e.target.value)}
                      />
                      <Button size="sm" variant="secondary" onClick={handleMarkPaid} disabled={actionLoading}>
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Mark paid
                      </Button>
                    </>
                  )}
                  {detail.status === 'draft' && (
                    <Button size="sm" variant="destructive" onClick={handleVoid} disabled={actionLoading}>
                      <Ban className="h-4 w-4 mr-1" /> Void draft
                    </Button>
                  )}
                </div>

                <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2">Date</th>
                        <th className="text-left px-3 py-2">Role</th>
                        <th className="text-right px-3 py-2">Sched h</th>
                        <th className="text-right px-3 py-2">Rate</th>
                        <th className="text-right px-3 py-2">Total</th>
                        {detail.document_type !== 'credit_note' && (detail.status === 'sent' || detail.status === 'paid') && (
                          <th className="text-right px-3 py-2">Credit h</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {(detail.items || []).map((item) => (
                        <tr key={item.id}>
                          <td className="px-3 py-2">{formatDateTime(item.shift_start_at)}</td>
                          <td className="px-3 py-2">{item.role_required || '—'}</td>
                          <td className="px-3 py-2 text-right">{Number(item.hours_scheduled || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">{formatMoney(item.rate_amount)}</td>
                          <td className="px-3 py-2 text-right">{formatMoney(item.line_total)}</td>
                          {detail.document_type !== 'credit_note' && (detail.status === 'sent' || detail.status === 'paid') && (
                            <td className="px-3 py-2 text-right">
                              <Input
                                type="number"
                                min="0"
                                max={Number(item.hours_billed || item.hours_scheduled || 0)}
                                step="0.01"
                                className="h-8 w-20 ml-auto text-right"
                                aria-label={`Credit hours for ${item.role_required || 'invoice line'}`}
                                value={creditHours[item.attendance_record_id] || ''}
                                onChange={(e) => setCreditHours((prev) => ({ ...prev, [item.attendance_record_id]: e.target.value }))}
                              />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {detail.document_type !== 'credit_note' && (detail.status === 'sent' || detail.status === 'paid') && (
                  <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-3 space-y-3">
                    <div>
                      <p className="text-sm font-medium text-purple-950">Correct an issued invoice safely</p>
                      <p className="text-xs text-purple-800 mt-1">
                        Never resend different information under the same invoice number. Enter the exact hours to credit on the affected line(s); CareOps creates a linked credit note with its own reference.
                      </p>
                    </div>
                    <Input
                      value={creditReason}
                      onChange={(e) => setCreditReason(e.target.value)}
                      placeholder="Reason, e.g. 30 minutes late / wrong hours"
                      aria-label="Credit note reason"
                    />
                    <Button size="sm" variant="outline" onClick={handleCreateCreditNote} disabled={actionLoading}>
                      Create linked credit note
                    </Button>
                    {correctionSummary?.credit_notes?.length > 0 && (
                      <div className="text-xs text-purple-900 space-y-1">
                        {correctionSummary.credit_notes.map((note) => (
                          <p key={note.id}>
                            {note.reference} · {note.status} · {formatMoney(note.total_amount)} · {note.reason}
                          </p>
                        ))}
                        <p className="font-medium">
                          Remaining to credit before corrected rebilling: {formatMoney(correctionSummary.remaining_to_credit)}
                        </p>
                      </div>
                    )}
                    {correctionSummary?.can_release_for_rebilling && (
                      <Button size="sm" onClick={handleReleaseForRebilling} disabled={actionLoading}>
                        Release fully credited shifts for corrected rebilling
                      </Button>
                    )}
                  </div>
                )}

                {detail.document_type !== 'credit_note' && detail.status === 'credited' && !correctionSummary?.replacement_invoice_id && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <p className="text-sm font-medium text-blue-900">Original invoice fully credited</p>
                    <p className="text-xs text-blue-800 mt-1 mb-3">
                      Generate a new linked draft from the original canonical attendance. It will receive a new invoice reference.
                    </p>
                    <Button size="sm" onClick={handleGenerateReplacement} disabled={actionLoading}>
                      Generate corrected replacement invoice
                    </Button>
                  </div>
                )}

                {detail.vat_note && (
                  <p className="text-xs text-text-muted">{detail.vat_note}</p>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}